import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { Book, Printer, DollarSign, Layers, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import Footer from "../../components/Footer";
import ServicesSection from "../../components/Services";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
// import bannerImg from "../../assets/book-banner.png"; // Add your banner image path here

export default function BookPrinting() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Book Printing
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              Whether you are printing corporate books, children's books, or coffee table books, our expert printing services guarantee high-quality results at affordable prices.
            </p>
            <Link to="/contact">
            <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Get Started Today
            </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Corporate Book Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Corporate Book Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Looking for professional corporate book printing services? Capital Offset provides top-notch book printing and binding services with a focus on design, quality, and precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Types of Book Printing */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Types of Book Printing
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Web Press Printing</h3>
              <p className="text-gray-600">
                Ideal for high-volume printing projects. Web press printing allows for the mass production of books with high consistency and speed.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Offset Printing</h3>
              <p className="text-gray-600">
                Offset printing provides high-quality color reproduction and is perfect for large runs of books where consistency is crucial.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Digital Printing</h3>
              <p className="text-gray-600">
                Perfect for short-run printing. Digital printing offers flexibility and cost-effectiveness for small batches of books.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Children's Book Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Children's Book Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Capital Offset, we specialize in printing children's books with vibrant colors, sturdy binding, and designs that capture the imagination. From fairy tales to educational books, we ensure each project is handled with care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Digital Book Printing */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Digital Book Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our digital book printing service provides a cost-effective solution for smaller print runs while maintaining high-quality results. Get your books printed with precise details and clarity at an affordable price.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Coffee Table Book Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Coffee Table Book Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Capital Offset specializes in coffee table book printing, offering premium quality and unique designs. Our printing services deliver books that make a statement and are a perfect addition to any home or office.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us for Book Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Why Choose Capital Offset for Book Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Capital Offset is the best choice for book printing. We offer a wide range of printing services, including corporate books, children’s books, coffee table books, and more. Our team ensures your books are printed with the highest standards and delivered on time.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Printer className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Expert Printing Technology</h3>
              <p className="text-gray-600">
                We utilize advanced printing technology to ensure the highest quality in every print run, whether digital, offset, or web press.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <DollarSign className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Affordable Pricing</h3>
              <p className="text-gray-600">
                We offer competitive pricing for high-quality book printing without compromising on excellence.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Layers className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Custom Design Options</h3>
              <p className="text-gray-600">
                Choose from various book styles and designs, or let our expert graphic designers help you create a custom look for your book.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Get Your Books Printed Today!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Ready to promote your business with a high-quality catalog? Reach out today to get your books printed!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>


      <ServicesSection />
      {/* Contact Form */}
      
      <ContactSection />
      <Footer />
    </div>
  );
}
